import { useIsomorphicLayoutEffect } from "@/hooks/Custom";
import { vehicleImagePath } from "@/utils";
import {
  Flex,
  FlexProps,
  Image,
  Img,
  Input,
  Modal,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { IoMdArrowDropdown } from "react-icons/io";
import Select from "react-select";

export type Select2OptionProps = {
  label: string;
  value: string;
};

export interface Select2Props extends FlexProps {
  OptionsData: Select2OptionProps[];
  Selected: string | number | boolean;
  Default?: string | number | boolean;
  HasImg?: boolean;
  setSelected?: (value: any) => void;
  Callback?: any;
  Trigger?: any;
  HasNullOption?: boolean;
  Placeholder?: string;
  IsLast?: boolean;
  Submiter?: any;
  Skew?: boolean;
  SkewAngle?: string;
  PopAdjust?: string;
  MarginR?: string;
  Border?: string;
  ScrollAdj?: boolean;
  customW?: string;
}

export function Select2({
  customW,
  Border,
  OptionsData,
  Selected,
  Default,
  HasImg,
  setSelected,
  Callback,
  Placeholder,
  Trigger,
  HasNullOption,
  IsLast,
  Submiter,
  Skew,
  SkewAngle,
  PopAdjust,
  MarginR,
  ScrollAdj,
  ...rest
}: Select2Props) {
  const [IsOpen, setIsOpen] = useState<boolean>(false);

  const [Filter, setFilter] = useState<string>("");

  const InputRef = useRef<HTMLInputElement>(null);
  const OptionRef = useRef<HTMLDivElement>(null);
  const PopoverRef = useRef(null);
  const SearchInputRef = useRef<HTMLInputElement>(null);

  function handleClose() {
    setIsOpen(false);
  }

  useIsomorphicLayoutEffect(() => {
    if (Boolean(Trigger)) {
      console.log("Trigger", Trigger);
      setIsOpen(true);
    }
  }, [Trigger]);

  return (
    <Flex
      justifyContent={"center"}
      alignItems={"center"}
      position={"relative"}
      borderRadius={"50px"}
      mr={MarginR}
      w={customW ? customW : "150px"}
      h={"40px"}
      border={"1px solid"}
      borderColor={"ll-gray.100"}
      bgColor={"ll-gray.600"}
    >
      <Flex position="absolute" w={"full"}>
        <Popover
          initialFocusRef={SearchInputRef}
          placement={"bottom-end"}
          onClose={() => {
            setIsOpen(false);
            setFilter("");
          }}
          closeOnBlur
          isOpen={IsOpen}
        >
          <PopoverTrigger>
            <Flex
              as={"button"}
              type={"button"}
              cursor="pointer"
              justifyContent="space-around"
              alignItems="center"
              onClick={() => setIsOpen((prev) => !prev)}
              w={"full"}
              h="30px"
            >
              {HasImg && (
                <Flex w={Selected ? "40px" : "40px"} ml={"5px"}>
                  <Image
                    onError={(e) =>
                      (e.currentTarget.src = "https://cdn.pneufree.com.br/CMS/carros/TOYOTA.png")
                    }
                    src={
                      Selected
                        ? vehicleImagePath(`${Selected}`)
                        : vehicleImagePath(Default as string)
                    }
                    height={"35px"}
                    objectFit={"contain"}
                    alt="Fabricante"
                  />
                </Flex>
              )}
              <Flex w={"full"} justifyContent={"start"} alignItems={"center"} pl={"15px"}>
                <Text color={"ll-gray.300"} cursor="pointer" fontSize="text1">
                  {Selected ?? Placeholder}
                </Text>
              </Flex>
              <Flex
                justifyContent={"center"}
                alignItems={"center"}
                minW={"28px"}
                minH={"28px"}
                bgColor={"ll-gray.100"}
                borderRadius={"50px"}
                mr={"5px"}
              >
                <Img
                  w={"12px"}
                  src={"https://cdn.pneufree.com.br/LINGLONG/images/Geral/DESKTOP/arrow.svg"}
                />
              </Flex>
            </Flex>
          </PopoverTrigger>
          <PopoverContent
            bg={"white"}
            borderRadius={"25px"}
            style={{ boxShadow: "#0000002e 2px 5px 10px" }}
            w={customW ? customW : "150px"}
            marginRight={MarginR}
          >
            <PopoverBody>
              <Input
                focusBorderColor="ll-gray.300"
                borderColor={"ll-gray.200"}
                borderRadius={"25px"}
                type="text"
                w={"full"}
                maxH={rest.w && "25px"}
                mb={"5px"}
                p={"10px"}
                placeholder="Busque.."
                value={Filter}
                ref={SearchInputRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    OptionRef?.current?.click();
                  }
                }}
                onChange={(e) => setFilter(e.target.value.toLowerCase())}
              />
              <Flex
                direction={"column"}
                overflowY={"scroll"}
                overflowX={"hidden"}
                className={"Select2"}
                maxH={"300px"}
                w={"full"}
                ml={ScrollAdj ? ["0px", "-3px"] : null}
              >
                {HasNullOption && (
                  <Flex
                    py={"5px"}
                    onClick={() => {
                      setSelected(null);
                      if (Callback) {
                        Callback(null);
                      }
                      if (IsLast) {
                        setIsOpen(false);
                        Submiter(null);
                      }
                    }}
                    _hover={{ bg: "blue.100" }}
                    bg={Selected === null ? "blue.100" : "white"}
                    cursor={"pointer"}
                  >
                    <Text ml={"10px"}>{Placeholder ?? "TODOS"}</Text>
                  </Flex>
                )}
                {OptionsData.filter((s) => {
                  if (Filter.length > 0) {
                    return s.value.toLowerCase().includes(Filter);
                  } else {
                    return true;
                  }
                })?.map((opt, key) => (
                  <Flex
                    ref={key === 0 ? OptionRef : PopoverRef}
                    key={key}
                    py={"5px"}
                    id={opt.value}
                    onClick={() => {
                      setSelected(opt.value);
                      if (Callback) {
                        Callback(opt.value);
                      }
                      if (IsLast) {
                        setIsOpen(false);
                        Submiter(opt.value);
                      }
                    }}
                    _hover={{ bg: "blue.100" }}
                    bg={Selected === opt.value ? "blue.100" : "white"}
                    cursor={"pointer"}
                  >
                    {HasImg && (
                      <Image
                        src={vehicleImagePath(`${opt.label}`)}
                        alt={`${opt.label}`}
                        loading="lazy"
                        mr={"5px"}
                        w={"20px"}
                        h={"20px"}
                      />
                    )}
                    <Text>{opt.label}</Text>
                  </Flex>
                ))}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
    </Flex>
  );
}
